import * as React from "react";
import { useState, useRef, useEffect, useContext } from "react";
import "@aws-amplify/ui-react/styles.css";

// react-bootstrap components
import { Container } from "react-bootstrap";
import styles from "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  SendButton,
  InfoButton,
  Avatar,
  ConversationHeader,
  InputToolbox,
  TypingIndicator,
} from "@chatscope/chat-ui-kit-react";

import { promptAssistantChannelError } from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
import { AuthContext } from "contexts/authContext";
import { NotificationAlertContext } from "contexts/notificationAlertContext";

const Chat = ({
  clientId,
  clientEnvironmentId,
  channelId,
  inboundMessageIdempotencyKey,
  initialPrompt,
}) => {
  // Contexts
  const currentUser = useContext(AuthContext);
  const notify = useContext(NotificationAlertContext);
  const firstName = currentUser.firstName;
  const inputRef = useRef(null);
  const [msgInputValue, setMsgInputValue] = useState("");
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showLoader, setShowLoader] = React.useState(false);
  const [firstPrompt, setFirstPrompt] = React.useState(true);
  const [blockPrompt, setBlockPrompt] = React.useState(false);

  useEffect(() => {
    setMsgInputValue(initialPrompt);
  }, []);

  const sendPrompt = async (message) => {
    const oldMessages = [...messages];
    oldMessages.push({
      message,
      direction: "outgoing",
      sender: firstName,
      source: require("assets/img/blank_face.jpg"),
      position: "single",
    });
    setMessages(oldMessages);
    try {
      setShowLoader(true);
      const response = await API.graphql(
        graphqlOperation(promptAssistantChannelError, {
          prompt: message,
          session_id: currentSessionId,
          client_id: clientId,
          client_environment_id: clientEnvironmentId,
          channel_id: channelId,
          inbound_message_idempotency_key: inboundMessageIdempotencyKey,
        })
      );
      if (response.data?.promptAssistantChannelError?.success) {
        const responseBody = response.data?.promptAssistantChannelError?.body;
        setCurrentSessionId(responseBody.sessionId);
        oldMessages.push({
          message: responseBody.response,
          direction: "incoming",
          sender: "Mirth Assist",
          source: require("assets/img/Logo_Mirth.svg").default,
          position: "single",
        });
        if (firstPrompt) setFirstPrompt(false);
        
        // Handle allotment exceeded scenario
        if (responseBody.guardrail_action === "ALLOTMENT_EXCEEDED") {
          setBlockPrompt(true);
        } else if (
          responseBody.guardrail_action === "INTERVENED" &&
          responseBody.response ===
            "Sorry, I'm afraid I'll be unable to process your requests."
        ) {
          oldMessages.push({
            message:
              "You can reload this page to start a new session if you need to ask additional questions. Previous session context will be cleared when you reload the page.Thank you for using Mirth Assist.",
            direction: "incoming",
            sender: "Mirth Assist",
            source: require("assets/img/Logo_Mirth.svg").default,
            position: "single",
          });
          setBlockPrompt(true);
        }
      } else {
        notify("warning", response.data?.promptAssistantChannelError?.message);
        console.error(response.data?.promptAssistantChannelError?.message);
        setBlockPrompt(true);
      }
    } catch (error) {
      oldMessages.push({
        message:
          "You can reload this page to start a new session if you need to ask additional questions. Previous session context will be cleared when you reload the page.Thank you for using Mirth Assist.",
        direction: "incoming",
        sender: "Mirth Assist",
        source: require("assets/img/Logo_Mirth.svg").default,
        position: "single",
      });
      setBlockPrompt(true);
      console.error(error);
    } finally {
      setMessages(oldMessages);
      setShowLoader(false);
    }
  };

  const handleSend = (message) => {
    const cleanMessage = message.replace(/<br\s*\/?>/gi, '').trim();
    if (cleanMessage.length === 0) {
      notify("warning", "Please enter a message before sending.");
      return;
    }
    if (new Blob([message]).size > 5 * 1024 * 1024) {
      notify("warning", "Message exceeds 5 MB limit. Please reduce the message size.");
      return;
    }
    sendPrompt(message);
    setMsgInputValue("");
    inputRef.current?.focus();
  };

  return (
    <>
      <Container fluid>
        <div style={{ position: "relative", height: "70vh" }}>
          <MainContainer>
            <ChatContainer>
              <ConversationHeader>
                <Avatar
                  name="Mirth Assist"
                  src={require("assets/img/Logo_Mirth.svg").default}
                />
                <ConversationHeader.Content userName="Mirth Assist" info="AI" />
                {/* <ConversationHeader.Actions>
                  <InfoButton />
                </ConversationHeader.Actions> */}
              </ConversationHeader>
              <MessageList
                {...(showLoader
                  ? {
                      typingIndicator: (
                        <TypingIndicator content="Mirth Assist is typing" />
                      ),
                    }
                  : {})}
              >
                {messages.map((m, i) => (
                  <Message key={i} model={m}>
                    <Avatar name={m.sender} src={m.source} />
                  </Message>
                ))}
              </MessageList>
              <InputToolbox
                style={{
                  display: "flex",
                  flexDirection: "row",
                  borderTop: "1px dashed #d1dbe4",
                }}
              >
                <MessageInput
                  ref={inputRef}
                  disabled={blockPrompt || showLoader}
                  onChange={(msg) => setMsgInputValue(msg)}
                  value={msgInputValue}
                  sendButton={false}
                  attachButton={false}
                  onSend={handleSend}
                  style={{ flexGrow: 1, borderTop: 0, flexShrink: "initial" }}
                />
                <SendButton
                  onClick={() => handleSend(msgInputValue)}
                  disabled={
                    msgInputValue.length === 0 ||
                    new Blob([msgInputValue]).size > 5 * 1024 * 1024 ||
                    showLoader
                  }
                  style={{
                    fontSize: "1.2em",
                    marginLeft: 0,
                    paddingLeft: "0.2em",
                    paddingRight: "0.2em",
                  }}
                />
                {/* <InfoButton
                  onClick={() => alert("Important message!")}
                  style={{
                    fontSize: "1.2em",
                    paddingLeft: "0.2em",
                    paddingRight: "0.2em",
                  }}
                /> */}
              </InputToolbox>
            </ChatContainer>
          </MainContainer>
        </div>
      </Container>
    </>
  );
};

export default Chat;
